import React, { useState } from 'react';
import SEO from './SEO';
import { Link } from 'react-router-dom';

const Proposals: React.FC = () => {
  const [activeTab, setActiveTab] = useState('classic');

  const packages = [
    {
      id: 'classic',
      title: 'Classic Proposal Ride',
      tagline: 'Simple. Romantic. Effortless.',
      image: '/proposals/proposal2.jpg',
      color: 'from-stables-400 to-stables-600',
      price: 'Contact for pricing',
      includes: [
        '30-minute scenic horse ride through the farm trails',
        'Romantic picnic setup under the pecan trees',
        'Blankets & cushions',
        '"She said yes" rosette keepsake',
        'Mini picnic box with snacks & refreshments',
        'Basic proposal guidance (timing & flow)',
        'Set times available',
      ],
      perfect: 'A relaxed, heartfelt proposal in a peaceful farm setting.',
    },
    {
      id: 'signature',
      title: 'Signature Proposal Experience',
      tagline: 'Thoughtful details. Elevated romance.',
      image: '/proposals/proposal2.jpg',
      color: 'from-stables-500 to-stables-700',
      price: 'Contact for pricing',
      includes: [
        'Everything in Classic, plus:',
        'Styled picnic upgrade (low table, runners, bunting flags)',
        'Fresh bouquet of flowers (subject to availability)',
        '"She said yes" rosette keepsake',
        'Golden hour timing assistance',
        'Discreet proposal planning support',
      ],
      perfect: 'A beautifully styled proposal with meaningful, personal touches.',
    },
    {
      id: 'luxury',
      title: 'Luxury Proposal Experience',
      tagline: 'The full "wow" moment. Unforgettable from start to finish.',
      image: '/proposals/proposal2.jpg',
      color: 'from-stables-600 to-stables-800',
      price: 'Contact for pricing',
      includes: [
        'Everything in Signature, plus:',
        'Premium luxury picnic styling (subject to availability)',
        '"Marry Me" sign with proposal setup',
        'Secret proposal photographer (capturing the moment discreetly)',
        'Mini celebration cake',
        'Champagne / sparkling drink for celebration',
        'Deluxe charcuterie & dessert upgrade',
        'Priority golden hour booking',
        'Dedicated planning & coordination with our team',
      ],
      perfect: 'A once-in-a-lifetime, picture-perfect proposal with a true wow factor.',
    },
  ];

  const addOns = [
    'Additional photography / videography',
    'Custom signage or props',
    'Decorated horse (subtle floral/ribbon styling)',
    'Extra guests joining after the proposal',
    'Extended picnic time',
  ];

  const steps = [
    { number: '1', label: 'Book your date' },
    { number: '2', label: 'We help you plan the surprise (timing, setup, special touches)' },
    { number: '3', label: 'Enjoy a relaxed farm trail horse ride together' },
    { number: '4', label: 'Arrive at your romantic picnic setup' },
    { number: '5', label: "Pop the question — we'll take care of the rest" },
  ];

  const activePackage = packages.find(pkg => pkg.id === activeTab) || packages[0];

  return (
    <div className="min-h-screen">
      <SEO
        title="Horse Ride Proposal Experiences Pretoria East | The Stables"
        description="Plan the perfect marriage proposal at The Stables, Pretoria East. Romantic horse ride, private picnic, and full coordination. Classic, Signature and Luxury packages available."
        path="/proposals"
        image="https://www.thestableshorserides.co.za/proposals/proposal2.jpg"
      />
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center bg-gradient-to-br from-cream-50 via-stables-50 to-stables-100">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
          backgroundImage: "url('/proposals/proposal2.jpg')"
        }}></div>
        <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-transparent"></div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold mb-6">Proposal Experiences</h1>
          <p className="text-xl md:text-2xl mb-8 font-light">
            The moment they'll never forget — in a peaceful farm setting surrounded by horses and nature
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:info@thestableshorserides.co.za?subject=Proposal Experience Enquiry"
              className="bg-stables-600 hover:bg-stables-700 text-white px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300"
            >
              Enquire via Email
            </a>
            <Link
              to="/horse-ride-picnic"
              className="bg-white text-stables-800 hover:bg-stables-100 px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300"
            >
              View Picnics
            </Link>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 bg-cream-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-stables-800 mb-6">The Moment They'll Never Forget</h2>
          <p className="text-xl text-stables-700 leading-relaxed mb-6">
            Planning a proposal can feel overwhelming — but it doesn't have to be. At The Stables, we help
            you create a beautiful, seamless, and unforgettable proposal experience in a peaceful farm setting.
          </p>
          <p className="text-xl text-stables-700 leading-relaxed">
            From the quiet anticipation of your horse ride to the magic of your private picnic under the pecan
            trees, every detail is designed to lead up to one perfect question.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-earth-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-stables-800 mb-12 text-center">How It Works</h2>
          <div className="space-y-6">
            {steps.map((step) => (
              <div key={step.number} className="flex items-start gap-6 bg-white rounded-2xl p-6 shadow-md">
                <div className="bg-gradient-to-br from-stables-500 to-stables-600 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-lg">
                  {step.number}
                </div>
                <p className="text-stables-700 text-lg leading-relaxed self-center">{step.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Package Selection */}
      <section className="py-20 bg-cream-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-stables-800 mb-6">Choose Your Package</h2>
            <p className="text-xl text-stables-700 max-w-3xl mx-auto">
              From intimate and heartfelt to a fully curated luxury experience
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {packages.map((pkg) => (
              <button
                key={pkg.id}
                onClick={() => setActiveTab(pkg.id)}
                className={`px-6 py-3 rounded-lg font-semibold text-base transition-all duration-300 transform hover:scale-105 ${
                  activeTab === pkg.id
                    ? `bg-gradient-to-r ${pkg.color} text-white shadow-xl`
                    : 'bg-white text-stables-700 hover:bg-stables-50 border-2 border-stables-200'
                }`}
              >
                {pkg.title}
              </button>
            ))}
          </div>

          {/* Active Package */}
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h3 className="text-3xl font-bold text-stables-800 mb-1">{activePackage.title}</h3>
              <p className="text-stables-500 italic mb-6">{activePackage.tagline}</p>
              <div className="space-y-3 mb-8">
                {activePackage.includes.map((item, index) => (
                  <div key={index} className={`flex items-start gap-3 bg-white rounded-xl p-4 shadow-sm ${index === 0 && activePackage.id !== 'classic' ? 'font-semibold text-stables-800' : ''}`}>
                    {!(index === 0 && activePackage.id !== 'classic') && (
                      <div className="w-2 h-2 bg-stables-600 rounded-full flex-shrink-0 mt-2"></div>
                    )}
                    <span className="text-stables-700">{item}</span>
                  </div>
                ))}
              </div>
              <div className="bg-stables-50 border border-stables-200 rounded-2xl p-5 mb-8">
                <p className="text-stables-700"><span className="font-semibold text-stables-800">Perfect for:</span> {activePackage.perfect}</p>
              </div>
              <a
                href="mailto:info@thestableshorserides.co.za?subject=Proposal Experience Enquiry"
                className="inline-block bg-stables-600 hover:bg-stables-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-300"
              >
                Enquire via Email
              </a>
            </div>
            <div className="relative group">
              <div className={`absolute inset-0 bg-gradient-to-r ${activePackage.color} rounded-3xl sm:rotate-3 sm:group-hover:rotate-6 transition-transform duration-300`}></div>
              <img
                src={activePackage.image}
                alt={activePackage.title}
                className="relative rounded-3xl shadow-2xl sm:-rotate-3 sm:group-hover:-rotate-6 transition-transform duration-300 w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Add-Ons */}
      <section className="py-20 bg-earth-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-stables-800 mb-4 text-center">Optional Add-Ons</h2>
          <p className="text-xl text-stables-700 text-center mb-12">Available for all packages</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {addOns.map((addOn, index) => (
              <div key={index} className="flex items-center gap-4 bg-white rounded-2xl p-5 shadow-md">
                <div className="w-3 h-3 bg-stables-600 rounded-full flex-shrink-0"></div>
                <span className="text-stables-700">{addOn}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Planning Support */}
      <section className="py-20 bg-cream-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="bg-white rounded-3xl p-12 shadow-xl border border-stables-200">
            <h2 className="text-3xl font-bold text-stables-800 mb-4">Planning Support</h2>
            <p className="text-xl text-stables-700 leading-relaxed">
              Not sure how to pull it all together? We'll guide you every step of the way — from choosing
              the right moment to coordinating the surprise on the day.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-stables-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-4xl font-bold mb-6">Ready to Plan the Perfect Proposal?</h2>
          <p className="text-xl mb-8">
            Let's create a moment they'll say yes to — contact us today to start planning.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:info@thestableshorserides.co.za?subject=Proposal Experience Enquiry"
              className="bg-cream-50 text-stables-800 hover:bg-cream-100 px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300"
            >
              Email Us
            </a>
            <a
              href="tel:+27699464074"
              className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg text-base font-semibold hover:bg-white hover:text-stables-600 transition-colors duration-300"
            >
              Call Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Proposals;
